import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useClub } from '../../app/club';
import { useRepo } from '../../app/services';
import { useAccessRules, useClubMutation, useDoors, useGroups, useLoanCards } from '../../data/hooks';
import { Badge, Button, Dialog, EmptyState, ErrorState, Field, Loading, PageHeader, Panel, useToast, type Tone } from '../../components/ui';
import { can } from '../../lib/roles';
import { errorText } from '../../lib/errors';
import { endOfDayIso } from '../../lib/expiry';
import { fmtWeekdayDateTime, toDateInput } from '../../lib/format';
import { weekdaysLabel, windowLabel } from '../../lib/weekdays';
import type { LoanCardRow } from '../../lib/types';

/**
 * Uitleenpassen voor gasten (persona-besluit 03-11-2026). De pas staat op de sloten van zijn groep en opent alleen binnen
 * de tijden van die groep. Uitlenen en innemen leggen vast wie de pas heeft; zonder gateway verandert er op het slot niets.
 */
function statusOf(r: LoanCardRow): { tone: Tone; text: string } {
  if (r.card_status === 'blocked') return { tone: 'red', text: 'Geblokkeerd' };
  if (r.card_status !== 'active') return { tone: 'yellow', text: 'Nog niet ingeleerd' };
  if (r.loan_id && r.overdue) return { tone: 'red', text: 'Te laat' };
  if (r.loan_id) return { tone: 'ink', text: 'Uitgeleend' };
  return { tone: 'green', text: 'Beschikbaar' };
}

export function LoanCardsPage() {
  const { orgId, role, readOnly } = useClub();
  const cards = useLoanCards(orgId);
  const groups = useGroups(orgId);
  const rules = useAccessRules(orgId);
  const doors = useDoors(orgId);
  const [creating, setCreating] = useState(false);
  const [lending, setLending] = useState<LoanCardRow | null>(null);
  const [returning, setReturning] = useState<LoanCardRow | null>(null);

  const groupName = useMemo(() => new Map((groups.data ?? []).map((g) => [g.id, g.name])), [groups.data]);
  const doorName = useMemo(() => new Map((doors.data ?? []).map((d) => [d.id, d.name])), [doors.data]);

  const qs = [cards, groups, rules, doors];
  if (qs.some((q) => q.isLoading)) return <Loading />;
  const err = qs.find((q) => q.error)?.error;
  if (err) return <ErrorState error={err} />;

  const canLend = can('loan_card.lend', role) && !readOnly;
  const canCreate = can('loan_card.create', role) && !readOnly;
  const rows = cards.data!;

  /** "Kantine: za 08:00–18:00" per regel van de groepen van de pas. */
  const accessText = (r: LoanCardRow) =>
    rules
      .data!.filter((x) => r.group_ids.includes(x.group_id))
      .map((x) => `${doorName.get(x.door_id) ?? 'Deur'}: ${weekdaysLabel(x.weekdays)} ${windowLabel(x)}`)
      .join(' · ') || 'Nog geen deuren of tijden in de groep';

  return (
    <div className="stack-lg">
      <PageHeader
        title="Uitleenpassen"
        intro="Passen van de club voor gasten, zoals een bezoekend team of de scheidsrechter. Een uitleenpas opent alleen de deuren en tijden van zijn groep. Leg bij uitlenen vast wie hem heeft; neem hem na afloop weer in."
        actions={
          canCreate && (
            <Button variant="primary" onClick={() => setCreating(true)}>
              Nieuwe uitleenpas
            </Button>
          )
        }
      />
      <Panel>
        {rows.length === 0 ? (
          <EmptyState title="Nog geen uitleenpassen">
            {canCreate
              ? 'Maak een uitleenpas aan in een groep (bijvoorbeeld "Gasten") met de deuren en tijden waarop gasten binnen mogen. De installateur leert de pas daarna in op de sloten.'
              : 'De clubbeheerder maakt uitleenpassen aan.'}
          </EmptyState>
        ) : (
          <div className="table-wrap">
            <table className="data stack-mobile">
              <thead>
                <tr>
                  <th scope="col">Pas</th>
                  <th scope="col">Opent</th>
                  <th scope="col">Bij</th>
                  <th scope="col">Status</th>
                  <th scope="col">
                    <span className="sr-only">Acties</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const st = statusOf(r);
                  return (
                    <tr key={r.person_id}>
                      <td className="cell-main">
                        <Link className="cell-title primary" to={`/c/${orgId}/personen/${r.person_id}`}>
                          {r.label}
                        </Link>
                        <span className="cell-sub">
                          {r.group_ids
                            .map((g) => groupName.get(g))
                            .filter(Boolean)
                            .join(', ')}
                        </span>
                      </td>
                      <td data-label="Opent">
                        <span className="small">{accessText(r)}</span>
                      </td>
                      <td data-label="Bij">
                        {r.loan_id ? (
                          <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
                            {r.guest_name}
                            {r.guest_club && <span className="cell-sub">{r.guest_club}</span>}
                            <span className="cell-sub">terug {fmtWeekdayDateTime(r.due_at!)}</span>
                          </span>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td data-label="Status">
                        <Badge tone={st.tone}>{st.text}</Badge>
                      </td>
                      <td>
                        {canLend && r.loan_id && (
                          <Button size="sm" onClick={() => setReturning(r)}>
                            Innemen
                          </Button>
                        )}
                        {canLend && !r.loan_id && r.card_status === 'active' && (
                          <Button size="sm" variant="primary" onClick={() => setLending(r)}>
                            Uitlenen
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      <p className="muted small">
        Komt een pas niet terug? De clubbeheerder blokkeert hem op de pagina van de pas (Pas blokkeren). Het slot weigert hem zodra een
        clubbeheerder of installateur met de Antreja-app bij de deur is geweest.
      </p>

      {creating && (
        <CreateDialog orgId={orgId} groups={(groups.data ?? []).filter((g) => !g.archived_at)} onClose={() => setCreating(false)} />
      )}
      {lending && <LendDialog orgId={orgId} card={lending} onClose={() => setLending(null)} />}
      {returning && <ReturnDialog orgId={orgId} card={returning} onClose={() => setReturning(null)} />}
    </div>
  );
}

function CreateDialog({ orgId, groups, onClose }: { orgId: string; groups: { id: string; name: string }[]; onClose: () => void }) {
  const repo = useRepo();
  const toast = useToast();
  const [label, setLabel] = useState('');
  const [groupId, setGroupId] = useState(groups.find((g) => /gast/i.test(g.name))?.id ?? '');
  const [error, setError] = useState<string | null>(null);
  const save = useClubMutation(orgId, (v: { groupId: string; label: string }) => repo.createLoanCard(v.groupId, v.label));
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title="Nieuwe uitleenpas"
      description="De pas krijgt de deuren en tijden van de groep die je kiest. Daarna leert de installateur de pas in met de Antreja-app (bij Installeren, stap Pas inleren)."
    >
      <form
        className="stack"
        noValidate
        onSubmit={async (e) => {
          e.preventDefault();
          if (!groupId) return setError('Kies een groep.');
          if (label.trim().length < 2) return setError('Geef de pas een naam, bijvoorbeeld "Uitleenpas 1" (zoals op de pas staat).');
          setError(null);
          try {
            await save.mutateAsync({ groupId, label: label.trim() });
            toast(`${label.trim()} aangemaakt. Laat hem nu inleren door de installateur.`);
            onClose();
          } catch (err) {
            setError(errorText(err, 'Niet gelukt.'));
          }
        }}
      >
        <Field label="Naam van de pas" hint="Zet dezelfde naam of hetzelfde nummer op de pas zelf.">
          {(p) => (
            <input
              {...p}
              className="input"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Uitleenpas 1"
              maxLength={100}
            />
          )}
        </Field>
        <Field label="Groep" hint="Bijvoorbeeld een groep Gasten met de kleedkamers op zaterdag.">
          {(p) => (
            <select {...p} className="input" value={groupId} onChange={(e) => setGroupId(e.target.value)}>
              <option value="">Kies een groep</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          )}
        </Field>
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}
        <div className="dialog-actions">
          <Button onClick={onClose}>Annuleren</Button>
          <Button variant="primary" type="submit" disabled={save.isPending}>
            Aanmaken
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function LendDialog({ orgId, card, onClose }: { orgId: string; card: LoanCardRow; onClose: () => void }) {
  const repo = useRepo();
  const toast = useToast();
  const today = new Date();
  const max = new Date(today.getTime() + 7 * 86400_000);
  const [name, setName] = useState('');
  const [club, setClub] = useState('');
  const [date, setDate] = useState(toDateInput(today));
  const [error, setError] = useState<string | null>(null);
  const save = useClubMutation(orgId, (v: { name: string; club: string | null; due: string }) =>
    repo.lendLoanCard(card.person_id, v.name, v.club, v.due),
  );
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={`${card.label} uitlenen`}
      description="Leg vast wie de pas heeft en wanneer hij terugkomt."
    >
      <form
        className="stack"
        noValidate
        onSubmit={async (e) => {
          e.preventDefault();
          if (name.trim().length < 2) return setError('Vul de naam van de gast in.');
          if (!date || date < toDateInput(today) || date > toDateInput(max)) return setError('Kies een inleverdatum binnen 7 dagen.');
          setError(null);
          try {
            await save.mutateAsync({
              name: name.trim(),
              club: club.trim() || null,
              due: endOfDayIso(date),
            });
            toast(`${card.label} uitgeleend aan ${name.trim()}.`);
            onClose();
          } catch (err) {
            setError(errorText(err, 'Niet gelukt.'));
          }
        }}
      >
        <Field label="Naam van de gast">
          {(p) => (
            <input
              {...p}
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Jan de Vries"
              maxLength={100}
              autoFocus
            />
          )}
        </Field>
        <Field label="Vereniging of team" hint="Bijvoorbeeld VV Gouda JO17-1 of scheidsrechter.">
          {(p) => <input {...p} className="input" value={club} onChange={(e) => setClub(e.target.value)} maxLength={100} />}
        </Field>
        <Field label="Terug op" hint="De pas werkt alleen binnen de tijden van zijn groep, ook als hij niet op tijd terug is.">
          {(p) => (
            <input
              {...p}
              className="input"
              type="date"
              min={toDateInput(today)}
              max={toDateInput(max)}
              value={date}
              onChange={(e) => setDate(e.target.value)}
              style={{ maxWidth: 220 }}
            />
          )}
        </Field>
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}
        <div className="dialog-actions">
          <Button onClick={onClose}>Annuleren</Button>
          <Button variant="primary" type="submit" disabled={save.isPending}>
            Uitlenen
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function ReturnDialog({ orgId, card, onClose }: { orgId: string; card: LoanCardRow; onClose: () => void }) {
  const repo = useRepo();
  const toast = useToast();
  const save = useClubMutation(orgId, () => repo.returnLoanCard(card.person_id));
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={`${card.label} innemen`}
      description={`Heb je de pas terug van ${card.guest_name}${card.guest_club ? ` (${card.guest_club})` : ''}?`}
    >
      {save.error && (
        <div className="alert alert-danger" role="alert">
          {errorText(save.error, 'Niet gelukt.')}
        </div>
      )}
      <div className="dialog-actions">
        <Button onClick={onClose}>Annuleren</Button>
        <Button
          variant="primary"
          disabled={save.isPending}
          onClick={async () => {
            try {
              await save.mutateAsync(undefined);
              toast(`${card.label} ingenomen.`);
              onClose();
            } catch {
              /* melding staat in de dialoog */
            }
          }}
        >
          Ingenomen
        </Button>
      </div>
    </Dialog>
  );
}
